/** @import { SupermarketId, CategoryFilter, OfferWeek, SortOption } from '../../types.js' */

const SEARCH_DEBOUNCE_MS = 300

function parseSemanticResults(data) {
  const names = new Set()
  for (const cluster of data.clusters ?? []) {
    for (const product of cluster.products ?? []) {
      names.add(product.name.toLowerCase())
    }
    if (cluster.name) names.add(cluster.name.toLowerCase())
  }
  return names.size > 0 ? names : null
}

async function executeSemanticSearch(query, controller, set) {
  try {
    const res = await fetch(`/api/v1/search?q=${encodeURIComponent(query)}`, { signal: controller.signal })
    if (!res.ok) throw new Error('Search failed')

    const matches = parseSemanticResults(await res.json())
    if (!controller.signal.aborted) {
      set({ semanticMatches: matches, semanticSearching: false })
    }
  } catch (err) {
    if (err.name !== 'AbortError') {
      console.warn('[SemanticSearch]', err.message)
      if (!controller.signal.aborted) {
        set({ semanticMatches: null, semanticSearching: false })
      }
    }
  }
}

export function filterSlice(set, get) {
  return {
    /** @type {Set<SupermarketId>} */
    selectedSupermarkets: new Set(),
    /** @type {CategoryFilter} */
    selectedCategory: 'all',
    /** @type {OfferWeek} */
    selectedWeek: 'current',
    searchQuery: '',
    /** @type {SortOption} */
    sortBy: 'relevance',

    /** @type {Set<string>|null} */
    semanticMatches: null,
    semanticSearching: false,
    _searchAbort: null,
    _searchTimer: null,

    toggleSupermarket: (id) => set(state => {
      const next = new Set(state.selectedSupermarkets)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return { selectedSupermarkets: next }
    }),

    setSelectedCategory: (category) => set({ selectedCategory: category }),
    setSelectedWeek: (week) => set({ selectedWeek: week }),
    setSearchQuery: (query) => {
      set({ searchQuery: query })
      get()._triggerSemanticSearch(query)
    },
    setSortBy: (sort) => set({ sortBy: sort }),

    resetFilters: () => {
      set({
        selectedSupermarkets: new Set(),
        selectedCategory: 'all',
        selectedWeek: 'current',
        searchQuery: '',
        sortBy: 'relevance',
      })
      get()._cancelSemanticSearch()
    },

    _cancelSemanticSearch: () => {
      const { _searchAbort, _searchTimer } = get()
      if (_searchTimer) clearTimeout(_searchTimer)
      if (_searchAbort) _searchAbort.abort()
      set({ semanticMatches: null, semanticSearching: false, _searchAbort: null, _searchTimer: null })
    },

    _triggerSemanticSearch: (query) => {
      const { _searchAbort, _searchTimer } = get()
      if (_searchTimer) clearTimeout(_searchTimer)
      if (_searchAbort) _searchAbort.abort()

      const q = query.trim()
      if (q.length < 2) {
        set({ semanticMatches: null, semanticSearching: false })
        return
      }

      set({ semanticSearching: true })
      const controller = new AbortController()

      const timer = setTimeout(() => executeSemanticSearch(q, controller, set), SEARCH_DEBOUNCE_MS)

      set({ _searchAbort: controller, _searchTimer: timer })
    },
  }
}
